import { ChatClient, type ChatThreadClient } from '@azure/communication-chat';
import { AzureCommunicationTokenCredential } from '@azure/communication-common';
import type { AcsChatConfig } from './AcsChatConfig';
import type { ChatMessage } from './ChatService';

// ============================================================================
// ACS Chat Service  (frontend-only responsibilities)
// ============================================================================
//
// Backend already handles: create user, issue token, create thread, add participants.
// Backend returns: { token, userId, threadId }
//
// This service only does:
//   1. initClient()         → connect to ACS with the backend-provided token
//   2. joinThread()         → attach to the backend-created thread
//   3. sendMessage()        → send a text message
//   4. getMessages()        → fetch message history
//   5. onMessageReceived()  → listen for real-time incoming messages
//   6. dispose()            → clean up resources
//
// ============================================================================

export class AcsChatService {
  private chatClient: ChatClient | null = null;
  private threadClient: ChatThreadClient | null = null;
  private credential: AzureCommunicationTokenCredential | null = null;
  private readonly config: AcsChatConfig;
  private isDisposed: boolean = false;

  constructor(config: AcsChatConfig) {
    this.config = config;
  }

  // --------------------------------------------------------------------------
  // Step 1: Initialise the Chat Client
  // --------------------------------------------------------------------------
  async initClient(): Promise<ChatClient> {
    if (this.isDisposed) {
      throw new Error('Chat service has been disposed');
    }

    this.credential = new AzureCommunicationTokenCredential(this.config.token);
    this.chatClient = new ChatClient(this.config.endpoint, this.credential);

    // Start real-time notifications (WebSocket)
    try {
      await this.chatClient.startRealtimeNotifications();
    } catch (error) {
      console.warn(
        '[AcsChatService] Failed to start real-time notifications:',
        error
      );
      // Continue even if real-time notifications fail
    }

    return this.chatClient;
  }

  // --------------------------------------------------------------------------
  // Step 2: Join the thread created by the backend
  // --------------------------------------------------------------------------
  joinThread(threadId: string): ChatThreadClient | null {
    if (this.isDisposed || !this.chatClient) {
      console.warn(
        '[AcsChatService] Cannot join thread – client not ready or disposed.'
      );
      return null;
    }

    this.threadClient = this.chatClient.getChatThreadClient(threadId);
    return this.threadClient;
  }

  // --------------------------------------------------------------------------
  // Step 3: Send a message
  // --------------------------------------------------------------------------
  async sendMessage(content: string): Promise<string> {
    if (this.isDisposed || !this.threadClient) {
      console.warn(
        '[AcsChatService] Cannot send message – not ready or disposed.'
      );
      return '';
    }

    // Encode portal type in display name so receivers can identify the sender's portal
    const encodedDisplayName = this.config.portalType
      ? `${this.config.displayName}|${this.config.portalType}`
      : this.config.displayName;

    const result = await this.threadClient.sendMessage(
      { content },
      { senderDisplayName: encodedDisplayName }
    );

    return result.id;
  }

  // --------------------------------------------------------------------------
  // Step 4: Fetch message history
  // --------------------------------------------------------------------------
  async getMessages(): Promise<ChatMessage[]> {
    if (this.isDisposed || !this.threadClient) {
      console.warn(
        '[AcsChatService] Cannot get messages – not ready or disposed.'
      );
      return [];
    }

    const messages: ChatMessage[] = [];
    const messagesIterator = this.threadClient.listMessages();

    for await (const msg of messagesIterator) {
      // Only include text messages (skip system / participant events)
      if (msg.type === 'text' && msg.content?.message) {
        const senderId =
          msg.sender && 'communicationUserId' in msg.sender
            ? msg.sender.communicationUserId
            : 'unknown';

        const isCurrentUser = senderId === this.config.userId;
        const { displayName, portalType } = parseDisplayName(
          msg.senderDisplayName || 'Unknown'
        );

        messages.push({
          id: msg.id,
          senderId,
          senderName: displayName,
          senderInitials: getInitials(displayName),
          message: msg.content.message,
          timestamp: msg.createdOn || new Date(),
          isCurrentUser,
          portalType,
        });
      }
    }

    // ACS returns newest first – reverse so oldest is first
    return messages.reverse();
  }

  // --------------------------------------------------------------------------
  // Step 5: Real-time message listener
  // --------------------------------------------------------------------------
  onMessageReceived(callback: (message: ChatMessage) => void): void {
    if (this.isDisposed || !this.chatClient) {
      console.warn(
        '[AcsChatService] Cannot listen for messages – client not ready or disposed.'
      );
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.chatClient.on('chatMessageReceived', (event: any) => {
      // Only process messages belonging to the current thread
      if (
        event.threadId &&
        this.threadClient &&
        event.threadId !== this.threadClient.threadId
      ) {
        return;
      }

      const senderId =
        event.sender && 'communicationUserId' in event.sender
          ? event.sender.communicationUserId
          : 'unknown';

      const isCurrentUser = senderId === this.config.userId;
      const { displayName, portalType } = parseDisplayName(
        event.senderDisplayName || 'Unknown'
      );

      const chatMessage: ChatMessage = {
        id: event.id,
        senderId,
        senderName: displayName,
        senderInitials: getInitials(displayName),
        message: event.message,
        timestamp: new Date(event.createdOn),
        isCurrentUser,
        portalType,
      };

      callback(chatMessage);
    });
  }

  // --------------------------------------------------------------------------
  // Step 6: Clean up
  // --------------------------------------------------------------------------
  async dispose(): Promise<void> {
    this.isDisposed = true;

    try {
      if (this.chatClient) {
        await this.chatClient.stopRealtimeNotifications();
        this.chatClient = null;
      }
    } catch (error) {
      console.warn(
        '[AcsChatService] Error stopping real-time notifications:',
        error
      );
    }

    try {
      if (this.credential) {
        this.credential.dispose();
        this.credential = null;
      }
    } catch (error) {
      console.warn('[AcsChatService] Error disposing credential:', error);
    }

    this.threadClient = null;
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------
  getCurrentUserId(): string {
    return this.config.userId;
  }
}

// ============================================================================
// Helper: Extract initials from a display name
// ============================================================================
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

/**
 * Parse encoded display name in format "Name|PortalType" back into
 * separate display name and portal type.
 */
function parseDisplayName(raw: string): {
  displayName: string;
  portalType: string | undefined;
} {
  const separatorIndex = raw.lastIndexOf('|');
  if (separatorIndex === -1) {
    return { displayName: raw, portalType: undefined };
  }
  const displayName = raw.substring(0, separatorIndex);
  const portalType = raw.substring(separatorIndex + 1);
  return { displayName, portalType };
}
